import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import PageHeader from '../components/PageHeader';
import { Briefcase, GraduationCap, MapPin, Clock, X, Upload, Send, FileText, Users } from 'lucide-react';
import Translate from '../components/Translate';
import { db } from '../firebase';
import { collection, onSnapshot, addDoc, serverTimestamp } from 'firebase/firestore';
import Swal from 'sweetalert2';
import { useTranslation } from 'react-i18next';
import { getLangText } from '../utils/langHelper';

const Careers = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const { i18n } = useTranslation();
  const lang = i18n.language;
  
  // Application Modal state
  const [selectedJob, setSelectedJob] = useState(null);
  const [name, setName] = useState('');
  const [contact, setContact] = useState('');
  const [email, setEmail] = useState('');
  const [experience, setExperience] = useState('');
  const [resumeData, setResumeData] = useState('');
  const [resumeName, setResumeName] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const unsub = onSnapshot(collection(db, 'jobs'), (snapshot) => {
      const items = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setJobs(items);
      setLoading(false);
    });
    return unsub;
  }, []);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate size (800 KB)
    if (file.size > 800 * 1024) {
      Swal.fire({
        title: 'File Too Large',
        text: 'Please select a resume file smaller than 800 KB.',
        icon: 'error',
        confirmButtonColor: '#2E7D32'
      });
      e.target.value = '';
      return;
    }

    setResumeName(file.name);
    const reader = new FileReader();
    reader.onload = (event) => {
      setResumeData(event.target.result);
      Swal.fire({
        toast: true,
        position: 'top-end',
        icon: 'success',
        title: 'Resume uploaded successfully',
        showConfirmButton: false,
        timer: 1500
      });
    };
    reader.readAsDataURL(file);
  };

  const handleApplySubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !contact.trim() || !email.trim() || !experience.trim() || !resumeData) {
      Swal.fire('Error', 'Please fill in all fields and upload your resume.', 'error');
      return;
    }

    setSubmitting(true);
    try {
      await addDoc(collection(db, 'applications'), {
        role: selectedJob.title,
        name: name.trim(),
        contact: contact.trim(),
        email: email.trim(),
        experience: experience.trim(),
        resume: resumeData,
        resumeName,
        status: 'Pending',
        createdAt: serverTimestamp()
      });

      Swal.fire({
        title: 'Application Sent!',
        text: 'Thank you for applying. We will review your application soon.',
        icon: 'success',
        confirmButtonColor: '#2E7D32'
      });

      // Clear & Close
      setSelectedJob(null);
      setName('');
      setContact('');
      setEmail('');
      setExperience('');
      setResumeData('');
      setResumeName('');
    } catch (error) {
      console.error('Error applying for job:', error);
      Swal.fire('Error', 'Could not submit your application. Please try again.', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-cream font-body selection:bg-emerald selection:text-white pb-20">
      <PageHeader 
        title={<Translate>Build Your Future With Us</Translate>} 
        subtitle={<Translate>Join a team of 2,000+ professionals committed to innovation, sustainability, and rural growth.</Translate>} 
      />

      {/* Open Positions */}
      <section className="py-20 bg-ivory">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10">
          <div className="mb-16">
            <span className="section-label block mb-4"><Translate>Opportunities</Translate></span>
            <h2 className="section-title mb-2"><Translate>Current Openings</Translate></h2>
            <p className="text-slate text-lg"><Translate>Find a role that matches your passion and skills.</Translate></p>
          </div>

          {loading ? (
            <div className="text-center py-10 text-slate"><Translate>Loading positions...</Translate></div>
          ) : (
            <div className="space-y-6">
              {jobs.map((job, idx) => {
                const jobTitle = getLangText(job, 'title', lang);
                const jobDept = getLangText(job, 'dept', lang);
                const jobType = getLangText(job, 'type', lang);
                const jobLocation = getLangText(job, 'location', lang);
                const jobSkills = getLangText(job, 'skills', lang);
                const jobQual = getLangText(job, 'qualification', lang);

                return (
                  <motion.div 
                    key={job.id || idx}
                    initial={{ opacity: 0, y: 15 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: idx * 0.05 }}
                    className="premium-card p-8 sm:p-10 bg-white flex flex-col lg:flex-row lg:items-center justify-between gap-6 group hover:shadow-premium-lg transition-all duration-300 rounded-3xl border border-gray-100"
                  >
                    <div className="space-y-4 max-w-[85%]">
                      <div>
                        <h3 className="text-lg sm:text-xl font-heading font-extrabold text-charcoal group-hover:text-emerald transition-colors leading-snug">
                          <Translate>{jobTitle}</Translate>
                        </h3>
                        <div className="flex flex-wrap gap-4 text-xs sm:text-sm text-slate font-body font-semibold mt-2">
                          <span className="flex items-center">
                            <Briefcase size={16} className="mr-1.5 text-emerald" /> 
                            <Translate>{jobDept}</Translate>
                          </span>
                          <span className="flex items-center">
                            <Clock size={16} className="mr-1.5 text-gold" /> 
                            <Translate>{jobType}</Translate>
                          </span>
                          <span className="flex items-center">
                            <MapPin size={16} className="mr-1.5 text-sage-dark" /> 
                            <Translate>{jobLocation}</Translate>
                          </span>
                          <span className="flex items-center bg-emerald/10 text-emerald text-xs px-2.5 py-0.5 rounded-full font-bold">
                            <Users size={14} className="mr-1" />
                            <Translate>Vacancies</Translate>: {job.vacancies || '1'}
                          </span>
                        </div>
                      </div>

                      {/* Skills Required */}
                      {jobSkills && (
                        <div className="space-y-1.5">
                          <span className="text-[11px] font-heading font-bold text-slate/70 uppercase tracking-wider block">
                            <Translate>Skills Required</Translate>
                          </span>
                          <div className="flex flex-wrap gap-2">
                            {jobSkills.split(',').map((skill, sIdx) => (
                              <span key={sIdx} className="bg-emerald/10 text-emerald text-xs px-3 py-1 rounded-lg font-bold border border-emerald/10">
                                <Translate>{skill.trim()}</Translate>
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Qualification */}
                      {jobQual && (
                        <div className="flex items-center gap-2 text-xs sm:text-sm text-slate pt-0.5">
                          <GraduationCap size={18} className="text-emerald flex-shrink-0" />
                          <span className="font-semibold">
                            <Translate>Qualification</Translate>: <span className="text-charcoal font-bold text-xs sm:text-sm"><Translate>{jobQual}</Translate></span>
                          </span>
                        </div>
                      )}
                    </div>
                    <button 
                      onClick={() => setSelectedJob({ ...job, title: jobTitle })}
                      className="btn-premium py-3 px-6 cursor-pointer flex items-center justify-center gap-2 flex-shrink-0 self-start lg:self-center"
                    >
                      <span><Translate>Apply Now</Translate></span>
                    </button>
                  </motion.div>
                );
              })}
            </div>
          )}

          <div className="mt-16 text-center">
            <p className="text-slate text-lg mb-6 font-body font-medium"><Translate>Don't see a matching role?</Translate></p>
            <button 
              onClick={() => setSelectedJob({ title: 'General Application' })}
              className="text-gold font-heading font-bold text-lg hover:underline decoration-2 underline-offset-8 cursor-pointer"
            >
              <Translate>Send us your Resume anyway</Translate>
            </button>
          </div>
        </div>
      </section>

      {/* Application Popup Modal */}
      <AnimatePresence>
        {selectedJob && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-charcoal/60 backdrop-blur-md"
          >
            <motion.div 
              initial={{ scale: 0.95, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.95, y: 20 }}
              className="bg-white rounded-3xl border border-gray-100 shadow-premium-xl max-w-lg w-full overflow-hidden relative"
            >
              {/* Modal Header */}
              <div className="bg-forest p-6 text-white relative">
                <div className="absolute inset-0 pattern-overlay opacity-30 pointer-events-none"></div>
                <button 
                  type="button"
                  onClick={() => setSelectedJob(null)}
                  className="absolute top-4 right-4 p-3 bg-white/10 hover:bg-white/30 rounded-full text-white transition-all cursor-pointer z-30 flex items-center justify-center"
                  aria-label="Close form"
                >
                  <X size={20} />
                </button>
                <div className="relative z-10 space-y-1">
                  <span className="text-[10px] font-heading font-bold text-gold-light uppercase tracking-wider block">
                    <Translate>Job Application Form</Translate>
                  </span>
                  <h3 className="text-xl font-heading font-extrabold">
                    {selectedJob.title}
                  </h3>
                </div>
              </div>

              {/* Modal Form */}
              <form onSubmit={handleApplySubmit} className="p-8 space-y-6">
                <div className="space-y-4">
                  <div className="space-y-1">
                    <label className="text-[10px] font-heading font-bold text-slate uppercase tracking-wider block"><Translate>Full Name</Translate></label>
                    <input
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Enter your name"
                      className="w-full bg-ivory border border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold/20 text-sm"
                      required
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <label className="text-[10px] font-heading font-bold text-slate uppercase tracking-wider block"><Translate>Contact Number</Translate></label>
                      <input
                        type="tel"
                        value={contact}
                        onChange={(e) => setContact(e.target.value)}
                        placeholder="Enter your contact"
                        className="w-full bg-ivory border border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold/20 text-sm"
                        required
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-[10px] font-heading font-bold text-slate uppercase tracking-wider block"><Translate>Email Address</Translate></label>
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="Enter your email"
                        className="w-full bg-ivory border border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold/20 text-sm"
                        required
                      />
                    </div>
                  </div>

                  <div className="space-y-1">
                    <label className="text-[10px] font-heading font-bold text-slate uppercase tracking-wider block"><Translate>Total Experience</Translate></label>
                    <input
                      type="text"
                      value={experience}
                      onChange={(e) => setExperience(e.target.value)}
                      placeholder="e.g. 3 Years in Sugar Production"
                      className="w-full bg-ivory border border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold/20 text-sm"
                      required
                    />
                  </div>

                  <div className="space-y-1">
                    <label className="text-[10px] font-heading font-bold text-slate uppercase tracking-wider block"><Translate>Resume File (PDF/Word)</Translate></label>
                    <div className="border border-dashed border-gray-250 hover:border-emerald/50 rounded-xl p-4 bg-ivory/50 transition-all duration-300 flex items-center justify-between cursor-pointer relative group">
                      <input
                        type="file"
                        accept="application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                        onChange={handleFileChange}
                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                        required
                      />
                      <div className="flex items-center gap-3">
                        <Upload size={18} className="text-gray-400 group-hover:text-emerald transition-colors" />
                        <span className="text-xs font-semibold text-slate">
                          {resumeName ? resumeName : <Translate>Select Resume File (Max 800 KB)</Translate>}
                        </span>
                      </div>
                      {resumeName && <FileText size={16} className="text-emerald" />}
                    </div>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full btn-premium flex items-center justify-center gap-2 py-4 cursor-pointer"
                >
                  <Send size={16} />
                  <span>{submitting ? <Translate>Submitting...</Translate> : <Translate>Submit Application</Translate>}</span>
                </button>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Careers;
